import type {
  AdminVideoDetail,
  AppConfig,
  ApplyCreatorInput,
  AuthIdentity,
  ChangePasswordInput,
  CreateCreatorInput,
  CreateVideoInput,
  Creator,
  CreatorApplication,
  CreatorApplicationStatus,
  FinalizeUploadInput,
  Job,
  LoginInput,
  ReviewApplicationInput,
  SelfUpdateCreatorInput,
  ThumbnailUploadUrlRequest,
  UpdateVideoInput,
  UploadUrlRequest,
  UploaderSummary,
  Video,
  VideoStatus,
  YoutubeImportInput,
} from "@coinfallsmp/shared";

export type AdminVideo = Video & { uploader: UploaderSummary | null };

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { error?: string }).error ?? `Request failed: ${res.status}`,
    );
  }
  return res.json() as Promise<T>;
}

// --- Auth (public — no session required to reach these) ---

export function getMe() {
  return request<AuthIdentity>("/auth/me");
}

export function getAppConfig() {
  return request<AppConfig>("/config");
}

export function login(input: LoginInput) {
  return request<AuthIdentity>("/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function logout() {
  return request<{ ok: true }>("/auth/logout", { method: "POST" });
}

export function applyForCreator(input: ApplyCreatorInput) {
  return request<CreatorApplication>("/auth/apply", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// --- Self-service (requires a logged-in session) ---

export function updateMe(input: SelfUpdateCreatorInput) {
  return request<Creator>("/admin/me", { method: "PATCH", body: JSON.stringify(input) });
}

export function changeMyPassword(input: ChangePasswordInput) {
  return request<{ ok: true }>("/admin/me/change-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getMyAvatarUploadUrl(input: ThumbnailUploadUrlRequest) {
  return request<{ uploadUrl: string; key: string }>("/admin/me/avatar-upload-url", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function finalizeMyAvatar(key: string) {
  return request<Creator>("/admin/me/finalize-avatar", {
    method: "POST",
    body: JSON.stringify({ key }),
  });
}

// --- Creator applications (admin only) ---

export function listApplications(status?: CreatorApplicationStatus | "all") {
  const query = status && status !== "all" ? `?status=${status}` : "";
  return request<{ items: CreatorApplication[] }>(`/admin/applications${query}`);
}

export function getApplication(id: string) {
  return request<CreatorApplication>(`/admin/applications/${id}`);
}

export function reviewApplication(id: string, input: ReviewApplicationInput) {
  return request<CreatorApplication>(`/admin/applications/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function activateApplication(
  id: string,
  input?: { username?: string; displayName?: string; email?: string },
) {
  return request<Creator>(`/admin/applications/${id}/activate`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
}

// --- Creators (admin only, except where noted) ---

export function listCreators() {
  return request<{ items: Creator[] }>("/admin/creators");
}

export function getCreator(id: string) {
  return request<Creator>(`/admin/creators/${id}`);
}

export function createCreator(input: CreateCreatorInput) {
  return request<Creator>("/admin/creators", { method: "POST", body: JSON.stringify(input) });
}

export function updateCreator(
  id: string,
  input: Partial<{
    username: string;
    displayName: string;
    email: string;
    bio: string | null;
    avatarUrl: string | null;
    status: "pending" | "approved" | "revoked";
    canUpload: boolean;
    isVerified: boolean;
  }>,
) {
  return request<Creator>(`/admin/creators/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function setCreatorAdmin(id: string, isAdmin: boolean) {
  return request<Creator>(`/admin/creators/${id}/set-admin`, {
    method: "POST",
    body: JSON.stringify({ isAdmin }),
  });
}

export function disableCreator(id: string) {
  return request<Creator>(`/admin/creators/${id}/disable`, { method: "POST" });
}

export function enableCreator(id: string) {
  return request<Creator>(`/admin/creators/${id}/enable`, { method: "POST" });
}

/** Returns a fresh temporary password ONCE — the caller must show it to the
 * admin a single time and never persist or re-display it afterward. */
export function resetCreatorPassword(id: string) {
  return request<{ temporaryPassword: string }>(`/admin/creators/${id}/reset-password`, {
    method: "POST",
  });
}

export function designateOfficial(id: string) {
  return request<Creator>(`/admin/creators/${id}/designate-official`, { method: "POST" });
}

export function getCreatorAvatarUploadUrl(id: string, input: ThumbnailUploadUrlRequest) {
  return request<{ uploadUrl: string; key: string }>(`/admin/creators/${id}/avatar-upload-url`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function finalizeCreatorAvatar(id: string, key: string) {
  return request<Creator>(`/admin/creators/${id}/finalize-avatar`, {
    method: "POST",
    body: JSON.stringify({ key }),
  });
}

// --- Videos ---

export function listVideos(status?: VideoStatus | "all") {
  const query = status && status !== "all" ? `?status=${status}` : "";
  return request<{ items: AdminVideo[] }>(`/admin/videos${query}`);
}

export function getVideoDetail(id: string) {
  return request<AdminVideoDetail>(`/admin/videos/${id}`);
}

export function createVideo(input: CreateVideoInput) {
  return request<Video>("/admin/videos", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateVideo(id: string, input: UpdateVideoInput) {
  return request<Video>(`/admin/videos/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteVideo(id: string) {
  return request<{ ok: true }>(`/admin/videos/${id}`, { method: "DELETE" });
}

export function getUploadUrl(id: string, input: UploadUrlRequest) {
  return request<{ uploadUrl: string; key: string }>(
    `/admin/videos/${id}/upload-url`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function getThumbnailUploadUrl(
  id: string,
  input: ThumbnailUploadUrlRequest,
) {
  return request<{ uploadUrl: string; key: string }>(
    `/admin/videos/${id}/thumbnail-upload-url`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function finalizeUpload(id: string, input: FinalizeUploadInput) {
  return request<Video>(`/admin/videos/${id}/finalize-upload`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function publishVideo(id: string) {
  return request<AdminVideoDetail>(`/admin/videos/${id}/publish`, {
    method: "POST",
  });
}

export function unpublishVideo(id: string) {
  return request<AdminVideoDetail>(`/admin/videos/${id}/unpublish`, {
    method: "POST",
  });
}

export function createYoutubeImport(input: YoutubeImportInput) {
  return request<Video>("/admin/youtube-imports", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listJobs() {
  return request<{ items: Job[] }>("/admin/jobs");
}

export function getJob(id: string) {
  return request<Job>(`/admin/jobs/${id}`);
}

/** Uploads directly to R2 via the presigned URL, reporting progress. */
export function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
}

/** Reads duration/width/height client-side from a video file before upload. */
export function readVideoMetadata(
  file: File,
): Promise<{ durationSeconds: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({
        durationSeconds: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      URL.revokeObjectURL(video.src);
    };
    video.onerror = () => reject(new Error("Couldn't read video metadata"));
    video.src = URL.createObjectURL(file);
  });
}
